import { HttpClient, httpResource } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  STYLE_VARIABLE_DEFS,
  StyleVariablesFile,
  StyleVariablesPatchRequest,
  StyleVariablesRecord,
  StyleVariablesResponse,
} from '@models/style-variables.model';

@Injectable({ providedIn: 'root' })
export class StyleVariablesService {
  private readonly http = inject(HttpClient);

  /** Reactive read for the admin page. */
  readonly current = httpResource<StyleVariablesResponse>(() => '/api/style-variables', {
    defaultValue: { variables: {}, updatedAt: null },
  });

  /** One-shot read used by the bootstrap initializer, before the app renders. */
  fetch(): Promise<StyleVariablesResponse> {
    return firstValueFrom(this.http.get<StyleVariablesResponse>('/api/style-variables'));
  }

  /**
   * Persists only what changed this session — a partial merge, not a full
   * replace. `variables` are upserted; `resets` are cleared entirely
   * (reverting to the compiled default), not set to the default's literal
   * value. Anything not mentioned in either is left as-is on the backend.
   */
  save(request: StyleVariablesPatchRequest) {
    return this.http.patch<StyleVariablesResponse>('/api/style-variables', request);
  }

  /** Clears all overrides. */
  reset() {
    return this.http.post<StyleVariablesResponse>('/api/style-variables/reset', {});
  }

  /**
   * Applies style variable overrides to the document root as inline custom
   * properties, which take precedence over the compiled defaults in
   * src/styles.scss. Absolute, not merge-only: any known variable missing
   * from `overrides` has its inline override removed, falling back to the
   * compiled default (or a parent variable, for the derived ones).
   */
  apply(overrides: StyleVariablesRecord): void {
    const root = document.documentElement;
    for (const def of STYLE_VARIABLE_DEFS) {
      const value = overrides[def.name];
      if (value) {
        root.style.setProperty(def.name, value);
      } else {
        root.style.removeProperty(def.name);
      }
    }
  }

  /** Returns only the keys where `current` differs from `reference`, keyed by variable name. */
  diff(current: StyleVariablesRecord, reference: StyleVariablesRecord): StyleVariablesRecord {
    const diff: StyleVariablesRecord = {};
    for (const def of STYLE_VARIABLE_DEFS) {
      const value = current[def.name];
      if (value !== reference[def.name]) {
        diff[def.name] = value;
      }
    }
    return diff;
  }

  /** Triggers a browser download of the given overrides as a JSON file. */
  download(variables: StyleVariablesRecord): void {
    const payload: StyleVariablesFile = { variables, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `style-variables-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Parses an uploaded style-variables JSON file. Accepts either the export
   * shape (`{ variables: {...}, exportedAt: ... }`) or a bare flat map
   * (`{ "--x": "y" }`), so a hand-written or hand-edited file works too.
   * Throws with a user-facing message on invalid JSON or shape.
   */
  parseFile(text: string): StyleVariablesRecord {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('That file is not valid JSON.');
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Expected a JSON object of variable names to values.');
    }

    const record = parsed as Record<string, unknown>;
    const variablesField = record['variables'];
    const source =
      typeof variablesField === 'object' && variablesField !== null && !Array.isArray(variablesField)
        ? (variablesField as Record<string, unknown>)
        : record;

    const result: StyleVariablesRecord = {};
    for (const [key, value] of Object.entries(source)) {
      if (typeof value === 'string' && value.trim() !== '') {
        result[key] = value;
      }
    }
    return result;
  }
}
